import { logger } from 'monitor';
import { useCallback, useEffect, useRef, useState } from 'react';

export const useWakeLock = () => {
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    const isWakeLockSupported = 'wakeLock' in navigator;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsSupported(isWakeLockSupported);

    if (!isWakeLockSupported) {
      logger.warn('Wake Lock API is not supported in this browser');
    }
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!isSupported) {
      logger.warn('Wake Lock API is not supported in this browser');
      return false;
    }

    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }

      const wakeLock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = wakeLock;

      wakeLock.addEventListener('release', () => {
        logger.info('Wake lock released');
        setIsActive(false);
        wakeLockRef.current = null;
      });

      setIsActive(true);
      logger.info('Wake lock activated');
      return true;
    } catch (error) {
      logger.error('Failed to request wake lock', error);
      setIsActive(false);
      return false;
    }
  }, [isSupported]);

  const releaseWakeLock = useCallback(async () => {
    if (!wakeLockRef.current) {
      return;
    }

    try {
      await wakeLockRef.current.release();
      wakeLockRef.current = null;
      setIsActive(false);
      logger.info('Wake lock released');
    } catch (error) {
      logger.error('Failed to release wake lock', error);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (wakeLockRef.current) {
        try {
          wakeLockRef.current.release();
        } catch (error) {
          logger.error('Failed to release wake lock on cleanup', error);
        }
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isActive && !wakeLockRef.current) {
        logger.info('Page became visible, reacquiring wake lock');
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActive, requestWakeLock]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    requestWakeLock();

    return () => {
      releaseWakeLock();
    };
  }, [requestWakeLock, releaseWakeLock]);

  return {
    isActive,
    isSupported,
    releaseWakeLock,
    requestWakeLock,
  };
};
